import type { FieldSchema, FormSchema, QuestionOption } from '@stepform';

interface DependantFieldSchema extends FieldSchema {
  dependsOn?: string;
  dependantOptions?: Record<string, QuestionOption[]>;
}

function optionsFor(
  fieldOptions: Record<string, QuestionOption[] | Record<string, QuestionOption[]>>,
  key: string
): QuestionOption[] {
  const value = fieldOptions[key];
  return Array.isArray(value) ? value : [];
}

function dependantOptionsFor(
  fieldOptions: Record<string, QuestionOption[] | Record<string, QuestionOption[]>>,
  key: string
): Record<string, QuestionOption[]> {
  const value = fieldOptions[key];
  return value && !Array.isArray(value) ? value : {};
}

export const PLACEHOLDER_FIELD_OPTIONS: Record<
  string,
  QuestionOption[] | Record<string, QuestionOption[]>
> = {
  OccurredAtWork: [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'unsure', label: 'Unsure' },
  ],
  WhoIsAffected: [
    { value: 'me', label: 'Me' },
    { value: 'someone_else', label: 'Someone else' },
    { value: 'group_of_people', label: 'A group of people' },
    { value: 'broader_issues', label: 'This is about broader issues in my workplace' },
    {
      value: 'connected_workplace',
      label: 'This is about a workplace I have connection to but do not work in',
    },
  ],
  PermissionToRaise: {
    someone_else: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    group_of_people: [
      { value: 'yes', label: 'Yes' },
      { value: 'at_least_one', label: 'Yes, from at least one person' },
      { value: 'no', label: 'No' },
    ],
    connected_workplace: [
      { value: 'yes', label: 'Yes' },
      { value: 'at_least_one', label: 'Yes, from at least one person' },
      { value: 'no', label: 'No' },
    ],
  },
  AffectedPersonsStillEmployed: [
    { value: '1', label: 'Yes' },
    { value: '0', label: 'No' },
  ],
  SelfStillEmployed: [
    { value: '1', label: 'Yes' },
    { value: '0', label: 'No' },
  ],
  DescribeConcern: [
    { value: 'work-related-stress', label: 'Work-related stress (e.g. work demands)' },
    { value: 'fatigue', label: 'Fatigue at work' },
    { value: 'bullying-harassment', label: 'Workplace bullying and harassment' },
    { value: 'violence', label: 'Violence at work' },
    { value: 'sexual-harassment', label: 'Sexual harassment' },
    { value: 'other', label: 'Other mental health incident/hazard' },
  ],
  AboutGender: [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'non-binary', label: 'Non-binary' },
    { value: 'prefer-not-to-say', label: 'Prefer not to say' },
  ],
  AboutEthnicGroup: [
    { value: 'new_zealand_european', label: 'New Zealand European' },
    { value: 'maori', label: 'Māori' },
    { value: 'samoan', label: 'Samoan' },
    { value: 'cook_islands_maori', label: 'Cook Islands Māori' },
    { value: 'tongan', label: 'Tongan' },
    { value: 'niuean', label: 'Niuean' },
    { value: 'chinese', label: 'Chinese' },
    { value: 'indian', label: 'Indian' },
    { value: 'other', label: 'Other' },
  ],
  ConsentContact: [
    { value: '1', label: 'Yes' },
    { value: '0', label: 'No' },
  ],
  ConsentInformBusiness: [
    { value: '1', label: 'Yes' },
    { value: '0', label: 'No' },
  ],
  ConcernSpecificPerson: [
    { value: '1', label: 'Yes' },
    { value: '0', label: 'No' },
  ],
  SpecificPersonStillEmployed: [
    { value: '1', label: 'Yes' },
    { value: '0', label: 'No' },
  ],
  RaisedWithAnotherOrganisation: [
    { value: 'police', label: 'Police' },
    { value: 'employment_relations_authority', label: 'Employment Relations Authority' },
    { value: 'mbie_mediation_service', label: 'MBIE Mediation Service' },
    { value: 'human_rights_commission', label: 'Human Rights Commission' },
    { value: 'union_representative', label: 'A union representative' },
    { value: 'employment_advocate', label: 'An employment advocate' },
    { value: 'lawyer', label: 'A lawyer' },
    { value: 'other', label: 'Other' },
    {
      value: 'no',
      label: 'No, my concern has not been raised with another agency or organisation',
    },
    { value: 'dont_know', label: "Don't know" },
  ],
};

export function buildSchema(
  fieldOptions: Record<string, QuestionOption[] | Record<string, QuestionOption[]>> = {},
  concernDetailsThingsToInclude = '<ul><li>Placeholder help text</li></ul>'
): FormSchema {
  const options = { ...PLACEHOLDER_FIELD_OPTIONS, ...fieldOptions };

  return {
    steps: [
      {
        id: 'step1',
        label: 'Step 1',
        title: 'Raise a mentally healthy work concern',
        navigationLabel: 'Raise your concern',
        sections: [
          {
            id: 'section-step1',
            showTopDivider: false,
            groups: [
              {
                id: 'group-triage',
                showTopDivider: false,
                fields: [
                  {
                    type: 'Radio',
                    id: 'OccurredAtWork',
                    label: 'Did this concern happen at work or while work was being done?',
                    options: optionsFor(options, 'OccurredAtWork'),
                  } as FieldSchema,
                  {
                    type: 'Callout',
                    id: 'callout-work-associated',
                    calloutId: 'callout-work-associated',
                    calloutStatus: 'warning',
                    text: '<p>WorkSafe can only consider concerns if they are associated with work.</p>',
                  },
                  {
                    type: 'Radio',
                    id: 'WhoIsAffected',
                    label: 'Who is affected by this concern?',
                    options: optionsFor(options, 'WhoIsAffected'),
                  } as FieldSchema,
                  {
                    type: 'RadioDependant',
                    id: 'PermissionToRaise',
                    dependsOn: 'WhoIsAffected',
                    label: 'Do you have permission to raise this concern?',
                    dependantOptions: dependantOptionsFor(options, 'PermissionToRaise'),
                  } as DependantFieldSchema,
                  {
                    type: 'Callout',
                    id: 'callout-permission-to-raise',
                    calloutId: 'callout-permission-to-raise',
                    calloutStatus: 'danger',
                    text: '<p>WorkSafe can only consider work-related mental health concerns if they were experienced by you or you have permission from the person who it is about.</p>',
                  },
                  {
                    type: 'Radio',
                    id: 'SelfStillEmployed',
                    label: 'Are you currently employed by the business?',
                    options: optionsFor(options, 'SelfStillEmployed'),
                  } as FieldSchema,
                ],
              },
              {
                id: 'group-persons-affected',
                showTopDivider: false,
                fields: [
                  { type: 'Text', id: 'AffectedPersonsName', label: 'Name/s' },
                  { type: 'Text', id: 'AffectedPersonsRelationship', label: 'Relationship to you' },
                  {
                    type: 'Radio',
                    id: 'AffectedPersonsStillEmployed',
                    label: 'Is at least one person still employed by the business?',
                    options: optionsFor(options, 'AffectedPersonsStillEmployed'),
                  } as FieldSchema,
                ],
              },
              {
                id: 'group-describe-concern',
                showTopDivider: false,
                fields: [
                  {
                    type: 'CheckList',
                    id: 'DescribeConcern',
                    label: 'Which best describes your mental health concern?',
                    supportText: 'Please select all options that are appropriate.',
                    options: optionsFor(options, 'DescribeConcern'),
                  } as FieldSchema,
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'step2',
        label: 'Step 2',
        title: 'Information about you, the person filling in this form',
        navigationLabel: 'About you',
        sections: [
          {
            id: 'section-step2',
            showTopDivider: false,
            groups: [
              {
                id: 'group-about-submitter',
                showTopDivider: false,
                fields: [
                  { type: 'Text', id: 'AboutFirstName', label: 'First name' },
                  { type: 'Text', id: 'AboutLastName', label: 'Last name' },
                  { type: 'Email', id: 'AboutEmail', label: 'Contact email' },
                  { type: 'Phone', id: 'AboutPhone', label: 'Contact number', optional: true },
                  {
                    type: 'Callout',
                    id: 'callout-contact-info',
                    text: '<p>WorkSafe will usually contact you via email in the first instance. We ask for your contact number in case we need to contact you.</p>',
                  },
                  {
                    type: 'DropDown',
                    id: 'AboutGender',
                    label: 'Gender',
                    options: optionsFor(options, 'AboutGender'),
                  } as FieldSchema,
                  {
                    type: 'CheckList',
                    id: 'AboutEthnicGroup',
                    label: 'What ethnic group do you belong to?',
                    supportText: 'Mark the space or spaces which apply to you.',
                    options: optionsFor(options, 'AboutEthnicGroup'),
                  } as FieldSchema,
                  {
                    type: 'TextArea',
                    id: 'AboutIwiAffiliations',
                    label: 'Iwi Affiliations',
                    optional: true,
                  },
                ],
              },
              {
                id: 'group-consent',
                showTopDivider: false,
                fields: [
                  {
                    type: 'Radio',
                    id: 'ConsentContact',
                    label: 'Can we contact you to discuss your concern?',
                    options: optionsFor(options, 'ConsentContact'),
                  } as FieldSchema,
                  {
                    type: 'Callout',
                    id: 'callout-consent-contact',
                    calloutId: 'callout-consent-contact',
                    calloutStatus: 'warning',
                    text: '<p>We may be limited in what we can do as we often contact people with further questions. As you fill out this form, please share any information that you think may be relevant.</p>',
                  },
                  {
                    type: 'Radio',
                    id: 'ConsentInformBusiness',
                    label:
                      'Are you comfortable with us telling the business involved who is raising the concern?',
                    options: optionsFor(options, 'ConsentInformBusiness'),
                  } as FieldSchema,
                  {
                    type: 'Callout',
                    id: 'callout-consent-inform-business',
                    calloutId: 'callout-consent-inform-business',
                    calloutStatus: 'warning',
                    text: '<p>We will make a note of this and keep your details confidential. If we reach a point where sharing your information with the business will help prevent harm, we will ask you first.</p>',
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'step3',
        label: 'Step 3',
        title: 'Your concern',
        navigationLabel: 'About your concern',
        sections: [
          {
            id: 'section-step3',
            showTopDivider: false,
            groups: [
              {
                id: 'group-concern-details',
                showTopDivider: false,
                fields: [
                  {
                    type: 'TextArea',
                    id: 'ConcernDetails',
                    label: 'Tell us as much as you feel comfortable about:',
                    supportText:
                      '<ul><li>your concern</li><li>the actions you have taken</li><li>how the business has responded</li></ul><p>We are looking for as much detail as possible. Please use specific examples where you can. For a list of things you may want to include, click the question mark.',
                    helpText: concernDetailsThingsToInclude,
                    rows: 10,
                  },
                  {
                    type: 'Radio',
                    id: 'ConcernSpecificPerson',
                    label: 'Is your concern about the actions of a specific person?',
                    options: optionsFor(options, 'ConcernSpecificPerson'),
                  } as FieldSchema,
                ],
              },
              {
                id: 'group-specific-person',
                showTopDivider: false,
                fields: [
                  { type: 'Text', id: 'SpecificPersonName', label: 'Name', optional: true },
                  { type: 'Text', id: 'SpecificPersonRelationship', label: 'Relationship to you' },
                  {
                    type: 'Radio',
                    id: 'SpecificPersonStillEmployed',
                    label: 'Are they still employed by the business?',
                    options: optionsFor(options, 'SpecificPersonStillEmployed'),
                  } as FieldSchema,
                ],
              },
              {
                id: 'group-action',
                showTopDivider: false,
                fields: [
                  {
                    type: 'TextArea',
                    id: 'WhatShouldBeDone',
                    label: 'What do you think should be done to stop this harm happening?',
                  },
                  {
                    type: 'CheckList',
                    id: 'RaisedWithAnotherOrganisation',
                    label: 'Has your concern been raised with another agency or organisation?',
                    supportText: 'Please select all options that are appropriate.',
                    options: optionsFor(options, 'RaisedWithAnotherOrganisation'),
                  } as FieldSchema,
                  {
                    type: 'Text',
                    id: 'RaisedWithAnotherOrganisationOther',
                    label: 'Which other agencies or organisations have you raised your concern with?',
                  },
                  {
                    type: 'TextArea',
                    id: 'OtherOrganisationActions',
                    label: 'What actions have they taken?',
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'step4',
        label: 'Step 4',
        title: "Tell us about the business you're raising a concern about",
        navigationLabel: 'About the workplace',
        sections: [
          {
            id: 'section-step4',
            showTopDivider: false,
            groups: [
              {
                id: 'group-business-details',
                showTopDivider: false,
                fields: [
                  { type: 'Text', id: 'BusinessName', label: 'Name of business' },
                  {
                    type: 'TextArea',
                    id: 'BusinessAddress',
                    label: 'Address where you work most often',
                  },
                  { type: 'Text', id: 'BusinessTownCity', label: 'Town/city' },
                  { type: 'Text', id: 'BusinessIndustry', label: 'Industry' },
                  {
                    type: 'TextArea',
                    id: 'BusinessOtherInfo',
                    label: 'Is there anything else you think we should know about the business?',
                    supportText:
                      'Please share anything you think could be important like performance management, organisational change, history of violence, changes to employment, relationship with management or people you think we should speak to.',
                  },
                ],
              },
              {
                id: 'group-anything-else',
                showTopDivider: false,
                fields: [
                  {
                    type: 'TextArea',
                    id: 'AnyOtherInformation',
                    label: 'Is there anything else you’d like to tell us?',
                    optional: true,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'step5',
        label: 'Step 5',
        title: 'Review and Submit',
        navigationLabel: 'Review',
        component: 'MentalHealthReview',
        sections: [],
      },
    ],
  };
}
